import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { X, AlertTriangle } from "lucide-react";

/**
 * Iter 130.ae · Modo concentración fiscal.
 *
 * Banner sticky en la parte superior del dashboard que aparece cuando
 * faltan ≤3 días para algún vencimiento fiscal o cuando hay vencidos.
 *
 * Cuenta atrás en vivo + enlace directo a la calculadora/calendario.
 * Descartable por sesión.
 */
const TAB_POR_MODELO = {
  "303": "iva",
  "390": "iva",
  "349": "iva",
  "347": "op347",
  "200": "is",
  "202": "is",
  "111": "calendario",
  "190": "calendario",
  "115": "calendario",
  "180": "calendario",
};

export default function FiscalConcentracionBanner() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("fiscal-concentracion-dismissed") === "1"; }
    catch { return false; }
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (dismissed) return;
    let alive = true;
    let timer = null;
    const tick = () => {
      api.get("/fiscal/sl/badge-count")
        .then((r) => alive && setData(r.data))
        .catch(() => {});
    };
    tick();
    timer = setInterval(tick, 60 * 1000);
    return () => { alive = false; if (timer) clearInterval(timer); };
  }, [dismissed]);

  if (dismissed || !data) return null;

  // Solo activamos el modo si hay vencidos o algo con ≤3 días
  const itemsCriticos = (data.top || []).filter((t) => t.dias <= 3);
  if (data.vencidos === 0 && itemsCriticos.length === 0) return null;

  const cerrar = () => {
    try { sessionStorage.setItem("fiscal-concentracion-dismissed", "1"); } catch {/* ignore */}
    setDismissed(true);
  };

  const irACalculadora = (modelo) => {
    const tab = TAB_POR_MODELO[modelo] || "calendario";
    navigate(`/fiscal-sl?tab=${tab}`);
  };

  // Caso 1: hay obligaciones vencidas
  if (data.vencidos > 0) {
    return (
      <div
        className="rounded-2xl border-2 p-4 mb-4 flex items-center gap-4 sticky top-2 z-30 shadow-lg animate-pulse-slow"
        style={{ borderColor: "#DC2626", background: "linear-gradient(135deg, rgba(220,38,38,0.12), rgba(220,38,38,0.04))" }}
        data-testid="fiscal-concentracion-banner"
      >
        <div className="text-3xl">🛑</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-extrabold" style={{ color: "#DC2626" }} data-testid="fiscal-concentracion-titulo">
            MODO CONCENTRACIÓN FISCAL · {data.vencidos} obligación{data.vencidos === 1 ? "" : "es"} VENCIDA{data.vencidos === 1 ? "" : "S"}
          </div>
          <div className="text-[11px] text-[var(--legatum-text-muted)]">
            Tienes declaraciones fuera de plazo. Cada día que pasa puede generar recargos AEAT.
          </div>
        </div>
        <button
          onClick={() => navigate("/fiscal-sl")}
          className="px-3 py-1.5 rounded text-xs font-bold text-white"
          style={{ background: "#DC2626" }}
          data-testid="fiscal-concentracion-cta"
        >
          Resolver →
        </button>
        <button onClick={cerrar} className="text-[var(--legatum-text-muted)] hover:text-[var(--legatum-text)] shrink-0" title="Ocultar esta sesión" data-testid="fiscal-concentracion-cerrar">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // Caso 2: vencimiento crítico en ≤3 días
  const top = itemsCriticos[0];
  const otras = itemsCriticos.length - 1;
  const color = top.dias <= 1 ? "#DC2626" : "#E08A1F";

  return (
    <div
      className="rounded-2xl border-2 p-4 mb-4 flex items-center gap-4 sticky top-2 z-30 shadow-lg"
      style={{ borderColor: color, background: `linear-gradient(135deg, ${color}15, ${color}05)` }}
      data-testid="fiscal-concentracion-banner"
    >
      <div className="text-3xl">
        <AlertTriangle className="h-8 w-8" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase font-extrabold tracking-wider px-2 py-0.5 rounded" style={{ background: color, color: "#fff" }}>
            MODO CONCENTRACIÓN
          </span>
          <span className="text-sm font-extrabold" style={{ color }} data-testid="fiscal-concentracion-titulo">
            Modelo {top.modelo} · {top.nombre}
          </span>
        </div>
        <div className="text-[11px] text-[var(--legatum-text-muted)] mt-0.5">
          Vence <b style={{ color }}>{top.dias === 0 ? "HOY" : top.dias === 1 ? "MAÑANA" : `en ${top.dias} días`}</b>
          {" "}({top.fecha})
          {otras > 0 && <span> · y {otras} obligación{otras === 1 ? "" : "es"} más en ≤3 días</span>}
        </div>
      </div>
      <button
        onClick={() => irACalculadora(top.modelo)}
        className="px-3 py-1.5 rounded text-xs font-bold text-white shrink-0"
        style={{ background: color }}
        data-testid="fiscal-concentracion-cta"
      >
        Ir a calculadora →
      </button>
      <button onClick={cerrar} className="text-[var(--legatum-text-muted)] hover:text-[var(--legatum-text)] shrink-0" title="Ocultar esta sesión" data-testid="fiscal-concentracion-cerrar">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
